import { useQuery } from '@tanstack/react-query';
import type { V1AutomationInputSpec } from '@/generated/hooks/grimoire/models';
import type { V1DefaultInputsHistoryEntry } from '@/generated/hooks/grimoire/models/v1DefaultInputsHistoryEntry';
import {
  listDefaultInputsHistory,
  getListDefaultInputsHistoryQueryKey,
} from '@/generated/hooks/grimoire/v1/api';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import {
  buildFileHistoryMap,
  buildInputHistoryMap,
} from '../utils/inputHistory';

/**
 * Fetches all pages of default inputs history for a given automation
 * and builds a per-input history map with the latest entry removed
 * (as it represents the current value).
 *
 * Resolves organization and workspace IDs from the user preferences store
 * so callers only need to provide the automation ID and input specs.
 *
 * @param automationId - The automation to fetch input history for
 * @param inputSpecs - The current input specifications to map history against
 * @returns The history entries, per-input history map, file history map, loading state, and error state
 */
export const useDefaultInputsHistory = (
  automationId: string,
  inputSpecs: V1AutomationInputSpec[],
) => {
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const isEnabled = !!organizationId && !!workspaceId && !!automationId;

  const {
    data: historyEntries = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: getListDefaultInputsHistoryQueryKey(
      organizationId,
      workspaceId,
      automationId,
    ),
    queryFn: async (): Promise<V1DefaultInputsHistoryEntry[]> => {
      const allEntries: V1DefaultInputsHistoryEntry[] = [];
      let pageToken: string | undefined;

      do {
        const response = await listDefaultInputsHistory(
          organizationId,
          workspaceId,
          automationId,
          { page_token: pageToken },
        );
        const entries = response.data.default_inputs_history_entries ?? [];
        allEntries.push(...entries);
        pageToken = response.data.next_page_token;
      } while (pageToken);

      return allEntries;
    },
    enabled: isEnabled,
  });

  const inputHistoryMap = buildInputHistoryMap(inputSpecs, historyEntries);
  const fileHistoryMap = buildFileHistoryMap(inputSpecs, historyEntries);

  return {
    historyEntries,
    inputHistoryMap,
    fileHistoryMap,
    isLoading,
    error,
  };
};
